import React from "react";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { LoadingState } from "@/components/webbuilder/ui/loaders/LoadingState";
import { ErrorState } from "@/components/webbuilder/ui/loaders/ErrorState";
import { PageHeader } from "@/components/webbuilder/layout/PageHeader";
import { TemplateInfoCard } from "@/components/webbuilder/features/onboarding/TemplateInfoCard";
import { TemplatePageSelectionGrid } from "@/components/webbuilder/features/onboarding/TemplatePageSelectionGrid";
import { PageCustomizationForm } from "@/components/webbuilder/features/forms/PageCustomizationForm";
import { PageActions } from "@/components/webbuilder/ui/elements/PageActions";

export default function TemplatePageSelectionPage() {
  const {
    template,
    loading,
    error,
    pageSelections,
    showCustomization,
    uploadingImages,
    uploadedImages,
    selectedPages,
    needsCustomization,
    templateName,
    togglePageSelection,
    updateCustomData,
    generateSlug,
    handleTitleChange,
    handleImageSelected,
    removeUploadedImage,
    handleContinue,
    handleBack,
  } = useTemplateSelection();

  if (loading) {
    return <LoadingState message="Loading template details..." />;
  }

  if (error || !template) {
    return <ErrorState error={error || "Template not found"} />;
  }

  const getHeaderTitle = () =>
    showCustomization ? "Customize Page" : "Select Pages";
  const getHeaderSubtitle = () =>
    showCustomization
      ? "Customize the details for your selected page"
      : `Choose which pages to import from "${templateName}"`;

  const getContinueText = () => {
    if (showCustomization) return "Import Page";
    if (needsCustomization) return "Customize Page";
    return `Import ${selectedPages.length} Page${selectedPages.length === 1 ? "" : "s"}`;
  };

  const getBackText = () =>
    showCustomization ? "Back to Selection" : "Cancel";

  return (
    <div className="w-full bg-gradient-page dark:bg-gradient-page-dark relative min-h-screen">
      <div className="max-w-4xl mx-auto px-8 py-12 pb-32">
        <PageHeader
          onBack={handleBack}
          title={getHeaderTitle()}
          subtitle={getHeaderSubtitle()}
        />

        {!showCustomization ? (
          <>
            <TemplateInfoCard template={template} />
            <TemplatePageSelectionGrid
              template={template}
              pageSelections={pageSelections}
              onTogglePageSelection={togglePageSelection}
            />
          </>
        ) : (
          <PageCustomizationForm
            template={template}
            selectedPages={selectedPages}
            uploadingImages={uploadingImages}
            uploadedImages={uploadedImages}
            onTitleChange={handleTitleChange}
            onUpdateCustomData={updateCustomData}
            onImageSelected={handleImageSelected}
            onRemoveUploadedImage={removeUploadedImage}
            generateSlug={generateSlug}
          />
        )}
      </div>

      <PageActions
        onContinue={handleContinue}
        onBack={handleBack}
        continueDisabled={selectedPages.length === 0}
        continueText={getContinueText()}
        backText={getBackText()}
      />
    </div>
  );
}
